// components/models/SeedVR2ControlPanel.tsx
import React, { useCallback } from 'react';
import Select from '../ui/Select';
import RangeControl from '../ui/RangeControl';
import { SeedVR2InputMegaPixels, SeedVR2Params } from '#types/types';
import { isSeedVR2OutputMegaPixels, SEEDVR2_INPUT_MEGAPIXEL_OPTIONS, SEEDVR2_OUTPUT_MEGAPIXEL_OPTIONS, SEEDVR2_STANDARD_POSTPROCESS } from '#lib/seedvr2';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';

const DEFAULT_SEEDVR2_PARAMS: SeedVR2Params = {
  prompt: '',
  inputMegaPixels: 2,
  outputMegaPixels: 32,
  seed: -1,
  ...SEEDVR2_STANDARD_POSTPROCESS,
};

export const getDefaultSeedVR2Params = (): SeedVR2Params => ({ ...DEFAULT_SEEDVR2_PARAMS });

const INPUT_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  ...SEEDVR2_INPUT_MEGAPIXEL_OPTIONS.map(megaPixels => ({ value: String(megaPixels), label: `${megaPixels} Mpx` })),
];

const OUTPUT_OPTIONS = SEEDVR2_OUTPUT_MEGAPIXEL_OPTIONS
  .map(megaPixels => ({ value: String(megaPixels), label: `${megaPixels} Mpx` }));

const SeedVR2ControlPanel: React.FC = React.memo(() => {
  const { kernel } = useKernel();
  const params = useKernelStore(
    state => state.modelParams[state.diffusionModel] as SeedVR2Params | undefined,
  );

  const updateParams = useCallback(<K extends keyof SeedVR2Params>(
    name: K,
    value: SeedVR2Params[K],
  ) => {
    const diffusionModel = kernel.current?.getDiffusionModel();
    if (!diffusionModel) return;
    kernel.current?.setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) return prevModelParams;
      return {
        ...prevModelParams,
        [diffusionModel]: { ...currentParams, [name]: value },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value,
      model_name: diffusionModel,
      source_component: 'SeedVR2ControlPanel',
    });
  }, [kernel]);

  const handleInputChange = useCallback((value: string) => {
    if (value === 'auto' || value === '0.5' || value === '1' || value === '2' || value === '5' || value === '10' || value === '16') {
      updateParams('inputMegaPixels', value === 'auto' ? 'auto' : Number(value) as SeedVR2InputMegaPixels);
    }
  }, [updateParams]);

  const handleOutputChange = useCallback((value: string) => {
    const parsed = Number(value);
    if (isSeedVR2OutputMegaPixels(parsed)) updateParams('outputMegaPixels', parsed);
  }, [updateParams]);

  const handleSeedChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    updateParams('seed', Math.floor(Number(event.target.value)));
  }, [updateParams]);

  if (!params) return null;

  return (
    <div className="space-y-4">
      <Select
        id="seedvr2-input-megapixels"
        label="Input Resolution"
        value={String(params.inputMegaPixels ?? 5)}
        options={INPUT_OPTIONS}
        onChange={handleInputChange}
      />
      <Select
        id="seedvr2-output-megapixels"
        label="Output Resolution"
        value={String(params.outputMegaPixels ?? 40)}
        options={OUTPUT_OPTIONS}
        onChange={handleOutputChange}
      />
      <RangeControl
        label="Seed"
        name="seed"
        min={-1}
        max={4_000_000_000}
        step={1}
        hideRange={true}
        value={typeof params.seed === 'number' ? params.seed : -1}
        onChange={handleSeedChange}
        description={params.seed === -1 ? 'Random seed.' : 'Fixed seed for a reproducible result.'}
      />
      <p className="text-xs text-[#777777]">
        Upscales the current generation area without a prompt. Credits follow the output target: 1 up to 16 Mpx, then +0.25 per additional 4 Mpx.
      </p>
    </div>
  );
});

SeedVR2ControlPanel.displayName = 'SeedVR2ControlPanel';

export default SeedVR2ControlPanel;
